import React, {useState, useEffect} from 'react';
import {useInfiniteQuery, useMutation, useQueryClient} from 'react-query';
import {Link} from 'react-router-dom';

import {useAxios} from 'src/utils/http';
import {
  BucketInfo,
  StatusResponse,
  BUCKET_ELIGIBLE_STATUSES,
  BUCKET_PHI_DETECTED_STATUSES,
} from './bucket-model';

interface DrillDataset {
  dataset_id: number;
  name: string;
  status: string;
  updated_at?: string;
  created_at?: string;
}

interface BatchListResponse {
  prefix: string;
  total: number;
  datasets: DrillDataset[];
  next_cursor: number | null;
  has_more: boolean;
}

const PAGE_SIZE = 100;

export interface BucketModalProps {
  prefix: string;
  bucket: BucketInfo;
  status: StatusResponse;
  onClose: () => void;
}

export const BucketModal = (props: BucketModalProps) => {
  const {prefix, bucket, onClose} = props;

  // Lock body scroll while modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <Header prefix={prefix} bucket={bucket} onClose={onClose} />
        <div className="p-6 space-y-4">
          <BucketSummary bucket={bucket} />
          {bucket.phiDetectedCount > 0 && (
            <PhiReviewBanner count={bucket.phiDetectedCount} bucket={bucket} />
          )}
          <DatasetListSection prefix={prefix} bucket={bucket} />
          <ActionSection prefix={prefix} bucket={bucket} />
        </div>
      </div>
    </div>
  );
};

const Header = ({
  prefix,
  bucket,
  onClose,
}: {
  prefix: string;
  bucket: BucketInfo;
  onClose: () => void;
}) => (
  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg">
    <div>
      <div className="text-xs text-gray-500 font-mono">{prefix}</div>
      <div className="text-lg font-bold text-gray-900">
        Bucket {bucket.index} — {bucket.label}
      </div>
    </div>
    <button
      onClick={onClose}
      className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
      aria-label="Close"
    >
      ×
    </button>
  </div>
);

const BucketSummary = ({bucket}: {bucket: BucketInfo}) => {
  const parts: string[] = [`${bucket.total} datasets`];
  if (bucket.eligibleCount > 0) {
    parts.push(
      `${bucket.eligibleCount} eligible for action (${BUCKET_ELIGIBLE_STATUSES[
        bucket.key
      ]?.join(', ')})`
    );
  }
  if (bucket.inProgressCount > 0 && bucket.action !== null) {
    parts.push(`${bucket.inProgressCount} in progress`);
  }
  if (bucket.phiDetectedCount > 0) {
    parts.push(
      `${bucket.phiDetectedCount} at ${BUCKET_PHI_DETECTED_STATUSES[
        bucket.key
      ]?.join(', ')} — human review`
    );
  }
  return <div className="text-sm text-gray-600">{parts.join(' · ')}</div>;
};

const PhiReviewBanner = ({
  count,
  bucket,
}: {
  count: number;
  bucket: BucketInfo;
}) => (
  <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
    <div className="text-sm font-medium text-amber-900">
      ⚠ {count} dataset(s) at <code>*_phi_detected</code> need human review
    </div>
    <div className="text-xs text-amber-800 mt-1">
      Automated OCR flagged possible PHI in these rows. They are{' '}
      <b>not included</b> in the action below — they need to be inspected in
      Review Datasets Bulk per the safety-layer policy.
    </div>
    <Link
      to="/admin/datasets/review-bulk"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block mt-2 text-sm text-amber-900 underline hover:text-amber-700"
    >
      Open Review Datasets Bulk →
    </Link>
    <div className="sr-only">bucket: {bucket.key}</div>
  </div>
);

const DatasetListSection = ({
  prefix,
  bucket,
}: {
  prefix: string;
  bucket: BucketInfo;
}) => {
  const api = useAxios();

  const query = useInfiniteQuery<BatchListResponse>(
    ['batch-list', prefix, bucket.key],
    ({pageParam = null}) =>
      api
        .get('/v1/admin/datasets/batch-list', {
          params: {
            prefix,
            bucket: bucket.key,
            after: pageParam,
            limit: PAGE_SIZE,
          },
        })
        .then(res => res.data as BatchListResponse),
    {
      getNextPageParam: lastPage =>
        lastPage.has_more ? lastPage.next_cursor : undefined,
      enabled: bucket.total > 0,
    }
  );

  if (bucket.total === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No datasets in this bucket.
      </div>
    );
  }

  const allDatasets = query.data?.pages.flatMap(p => p.datasets) ?? [];
  const loaded = allDatasets.length;

  return (
    <div>
      <div className="text-sm font-medium text-gray-900 mb-2">
        Datasets ({loaded} of {bucket.total} loaded)
      </div>
      {query.isError && (
        <div className="text-sm text-red-700">Failed to load datasets.</div>
      )}
      {query.isLoading && <div className="text-sm text-gray-500">Loading…</div>}
      {loaded > 0 && (
        <div className="border border-gray-200 rounded-md max-h-64 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-700">
                  ID
                </th>
                <th className="text-left px-3 py-2 font-medium text-gray-700">
                  Name
                </th>
                <th className="text-left px-3 py-2 font-medium text-gray-700">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {allDatasets.map(d => (
                <tr
                  key={d.dataset_id}
                  className="border-t border-gray-100 hover:bg-gray-50"
                >
                  <td className="px-3 py-1.5">
                    <Link
                      to={`/datasets/${d.dataset_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {d.dataset_id}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">{d.name}</td>
                  <td className="px-3 py-1.5 font-mono text-xs text-gray-600">
                    {d.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {query.hasNextPage && (
        <button
          className="mt-2 text-xs text-blue-600 hover:text-blue-800"
          onClick={() => query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
        >
          {query.isFetchingNextPage
            ? 'Loading…'
            : `Load more (${bucket.total - loaded} remaining)`}
        </button>
      )}
    </div>
  );
};

const ActionSection = ({
  prefix,
  bucket,
}: {
  prefix: string;
  bucket: BucketInfo;
}) => {
  switch (bucket.action) {
    case 'submit':
      return <SubmitAction prefix={prefix} bucket={bucket} />;
    case 'mark-clean':
      return <MarkCleanAction prefix={prefix} bucket={bucket} />;
    case 'advance':
      return <AdvanceAction prefix={prefix} bucket={bucket} />;
    default:
      return <InfoPanel bucket={bucket} />;
  }
};

const InfoPanel = ({bucket}: {bucket: BucketInfo}) => {
  const msg = infoMessageFor(bucket);
  if (!msg) return null;
  return (
    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
      <div className="text-sm font-medium text-blue-900">ℹ {msg.title}</div>
      <div className="text-xs text-blue-800 mt-1 whitespace-pre-line">
        {msg.body}
      </div>
    </div>
  );
};

function infoMessageFor(
  bucket: BucketInfo
): {title: string; body: string} | null {
  switch (bucket.key) {
    case '0_canceled_or_recalled':
      return {
        title: 'Terminal state — canceled / recalled',
        body: 'These datasets are out of the delivery flow. Reopening requires engineering intervention.',
      };
    case '2_metadata_fetching':
      return {
        title: 'Awaiting metadata pipeline',
        body: 'Airflow DAGs order_datasets (hourly) and dataset_delivery_master (check_metadata) process this state. Typical advance: within 1 hour.',
      };
    case '3_samples_processing_or_review':
      return {
        title: 'Use the Review page for samples_scanned',
        body: 'Samples-stage datasets must be reviewed per-study via the Review page so OCR-flagged studies get human eyes. Batch mark-clean has been disabled here to prevent skipping that review path.',
      };
    case '5_full_dataset_processing':
      return {
        title: 'Full dataset pipeline running',
        body: 'Airflow DAGs 03a_normalize_dicom, 03b_pack_full, 04_scan_full run sequentially. Monitor progress here — no manual action until dataset_scanned.',
      };
    case '7_full_delivering':
      return {
        title: 'Delivering to customer bucket',
        body: 'Airflow DAG 05_deliver_full is copying the full dataset into the customer green bucket. Terminal success is delivery_email_sent (bucket 8).',
      };
    case '8_delivered':
      return {
        title: 'Delivered',
        body: 'Customer has been notified. Order fulfilled.',
      };
    default:
      return null;
  }
}

// ──────────────────────────── action forms ────────────────────────────

const SubmitAction = ({
  prefix,
  bucket,
}: {
  prefix: string;
  bucket: BucketInfo;
}) => {
  const api = useAxios();
  const qc = useQueryClient();
  const [count, setCount] = useState('50');
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation<SubmitResult, Error, void>(
    () =>
      api
        .post('/v1/admin/datasets/batch-submit', {
          prefix,
          count: parseInt(count, 10),
        })
        .then(res => res.data as SubmitResult),
    {
      onSuccess: data => {
        setResult(data);
        setError(null);
        qc.invalidateQueries(['batch-status', prefix]);
        qc.invalidateQueries(['batch-list', prefix]);
      },
      onError: (err: any) => {
        setError(
          err?.response?.data?.message || err?.message || 'Submit failed'
        );
      },
    }
  );

  const parsedCount = parseInt(count, 10);
  const wouldDo = Math.min(parsedCount || 0, bucket.eligibleCount);
  const disabled =
    !parsedCount ||
    parsedCount < 1 ||
    parsedCount > 500 ||
    bucket.eligibleCount === 0 ||
    mutation.isLoading;

  return (
    <ActionBox
      title="⚡ Batch Submit"
      subtitle={`Flips up to N datasets from dataset_created to metadata_cust_requested. ${bucket.eligibleCount} eligible.`}
    >
      <WaveSizeInput value={count} onChange={setCount} />
      <button
        className="btn btn-primary"
        disabled={disabled}
        onClick={() => mutation.mutate()}
      >
        {mutation.isLoading ? 'Submitting…' : `Submit ${wouldDo || ''}`.trim()}
      </button>
      {error && <ErrorBox>{error}</ErrorBox>}
      {result && <SubmitResultBox result={result} />}
    </ActionBox>
  );
};

const MarkCleanAction = ({
  prefix,
  bucket,
}: {
  prefix: string;
  bucket: BucketInfo;
}) => {
  const api = useAxios();
  const qc = useQueryClient();
  const [count, setCount] = useState('50');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<MarkCleanResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation<MarkCleanResult, Error, void>(
    () =>
      api
        .post('/v1/admin/datasets/batch-mark-clean', {
          prefix,
          count: parseInt(count, 10),
        })
        .then(res => res.data as MarkCleanResult),
    {
      onSuccess: data => {
        setResult(data);
        setError(null);
        setConfirming(false);
        qc.invalidateQueries(['batch-status', prefix]);
        qc.invalidateQueries(['batch-list', prefix]);
      },
      onError: (err: any) => {
        setError(
          err?.response?.data?.message || err?.message || 'Mark clean failed'
        );
        setConfirming(false);
      },
    }
  );

  const parsedCount = parseInt(count, 10);
  const wouldDo = Math.min(parsedCount || 0, bucket.eligibleCount);
  const disabled =
    !parsedCount ||
    parsedCount < 1 ||
    parsedCount > 500 ||
    bucket.eligibleCount === 0 ||
    mutation.isLoading;

  const fanLabel =
    bucket.key === '3_samples_processing_or_review'
      ? 'samples review fan (samples_scanned → samples_marked_clean)'
      : 'dataset review fan (dataset_scanned → dataset_marked_clean)';

  return (
    <ActionBox
      title="⚡ Batch Mark Clean"
      subtitle={`${fanLabel}. You are recorded as reviewer. ${bucket.eligibleCount} eligible.`}
    >
      {!confirming && (
        <>
          <WaveSizeInput value={count} onChange={setCount} />
          <button
            className="btn btn-primary"
            disabled={disabled}
            onClick={() => setConfirming(true)}
          >
            Mark Clean {wouldDo || ''}
          </button>
        </>
      )}
      {confirming && (
        <ConfirmInline
          message={`Mark ${wouldDo} dataset(s) as clean and stamp you as reviewer?`}
          hint="This is the human-attestation step. Rows at *_phi_detected are left alone for Review Datasets Bulk."
          onCancel={() => setConfirming(false)}
          onConfirm={() => mutation.mutate()}
          loading={mutation.isLoading}
          confirmLabel={`Confirm & Mark Clean ${wouldDo}`}
        />
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      {result && <MarkCleanResultBox result={result} />}
    </ActionBox>
  );
};

const AdvanceAction = ({
  prefix,
  bucket,
}: {
  prefix: string;
  bucket: BucketInfo;
}) => {
  const api = useAxios();
  const qc = useQueryClient();
  const [count, setCount] = useState('50');
  const [targetEmail, setTargetEmail] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<AdvanceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation<AdvanceResult, Error, void>(
    () =>
      api
        .post('/v1/admin/datasets/batch-advance', {
          prefix,
          count: parseInt(count, 10),
          target_email: targetEmail.trim(),
        })
        .then(res => res.data as AdvanceResult),
    {
      onSuccess: data => {
        setResult(data);
        setError(null);
        setConfirming(false);
        qc.invalidateQueries(['batch-status', prefix]);
        qc.invalidateQueries(['batch-list', prefix]);
      },
      onError: (err: any) => {
        setError(
          err?.response?.data?.message || err?.message || 'Advance failed'
        );
        setConfirming(false);
      },
    }
  );

  const parsedCount = parseInt(count, 10);
  const wouldDo = Math.min(parsedCount || 0, bucket.eligibleCount);
  const disabled =
    !parsedCount ||
    parsedCount < 1 ||
    parsedCount > 500 ||
    !targetEmail.trim() ||
    bucket.eligibleCount === 0 ||
    mutation.isLoading;

  return (
    <ActionBox
      title="⚡ Batch Advance"
      subtitle={`Runs place-order → chown → quote-signed. ${bucket.eligibleCount} eligible (metadata_email_sent).`}
    >
      {!confirming && (
        <>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target owner email (for chown)
            </label>
            <input
              type="email"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="customer@example.com"
              value={targetEmail}
              onChange={e => setTargetEmail(e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1">
              Reassigns each dataset to this user — controls delivery folder
              routing via <code>user.aws_identifier</code>. Must exist in your
              tenant.
            </p>
          </div>
          <WaveSizeInput value={count} onChange={setCount} />
          <button
            className="btn btn-primary"
            disabled={disabled}
            onClick={() => setConfirming(true)}
          >
            Advance {wouldDo || ''}
          </button>
        </>
      )}
      {confirming && (
        <ConfirmInline
          message={`Advance ${wouldDo} dataset(s) to quotes_signed and chown to ${targetEmail.trim()}?`}
          hint="This creates a dataset_order row, reassigns ownership, and flips status in 3 atomic-per-dataset steps. Partial failures leave datasets at dataset_ordered (manual cleanup via CLI)."
          onCancel={() => setConfirming(false)}
          onConfirm={() => mutation.mutate()}
          loading={mutation.isLoading}
          confirmLabel={`Confirm & Advance ${wouldDo}`}
          destructive
        />
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
      {result && <AdvanceResultBox result={result} />}
    </ActionBox>
  );
};

// ──────────────────────────── shared ui bits ────────────────────────────

const ActionBox = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) => (
  <div className="p-4 bg-green-50 border border-green-200 rounded-md">
    <div className="text-sm font-semibold text-green-900">{title}</div>
    <div className="text-xs text-green-800 mb-3">{subtitle}</div>
    {children}
  </div>
);

const WaveSizeInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="mb-3">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Wave size (max 500)
    </label>
    <input
      type="number"
      className="w-32 border border-gray-300 rounded-md px-3 py-2 text-sm"
      value={value}
      min={1}
      max={500}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

const ConfirmInline = ({
  message,
  hint,
  onCancel,
  onConfirm,
  loading,
  confirmLabel,
  destructive,
}: {
  message: string;
  hint?: string;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
  confirmLabel: string;
  destructive?: boolean;
}) => (
  <div
    className={`p-3 rounded-md border ${
      destructive ? 'bg-red-50 border-red-300' : 'bg-amber-50 border-amber-300'
    }`}
  >
    <div
      className={`text-sm font-medium ${
        destructive ? 'text-red-900' : 'text-amber-900'
      }`}
    >
      {message}
    </div>
    {hint && (
      <div
        className={`text-xs mt-1 ${
          destructive ? 'text-red-800' : 'text-amber-800'
        }`}
      >
        {hint}
      </div>
    )}
    <div className="flex gap-2 mt-3">
      <button
        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
        onClick={onCancel}
        disabled={loading}
      >
        Cancel
      </button>
      <button
        className={`px-3 py-1.5 text-sm rounded-md text-white ${
          destructive
            ? 'bg-red-600 hover:bg-red-700'
            : 'bg-amber-600 hover:bg-amber-700'
        }`}
        onClick={onConfirm}
        disabled={loading}
      >
        {loading ? 'Working…' : confirmLabel}
      </button>
    </div>
  </div>
);

const ErrorBox = ({children}: {children: React.ReactNode}) => (
  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
    <div className="text-sm font-medium text-red-800">Error</div>
    <div className="text-sm text-red-700 mt-1">{children}</div>
  </div>
);

// ──────────────────────────── result boxes ────────────────────────────

interface SubmitResult {
  prefix: string;
  requested: number;
  submitted: {dataset_id: number; name: string}[];
  errors: {dataset_id: number; name: string; error?: string}[];
}

interface MarkCleanResult {
  prefix: string;
  requested: number;
  marked: {dataset_id: number; name: string; from?: string; to?: string}[];
  errors: {dataset_id: number; name: string; error?: string}[];
}

interface AdvanceResult {
  prefix: string;
  requested: number;
  advanced: {dataset_id: number; name: string; status?: string}[];
  errors: {
    dataset_id: number;
    name: string;
    status?: string;
    failed_step?: string;
    error?: string;
  }[];
}

const ResultShell = ({
  title,
  successCount,
  errorCount,
  requested,
  children,
}: {
  title: string;
  successCount: number;
  errorCount: number;
  requested: number;
  children?: React.ReactNode;
}) => (
  <div className="mt-3 p-3 bg-white border border-gray-300 rounded">
    <div className="text-sm font-semibold text-gray-900 mb-2">{title}</div>
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div>
        <div className="text-xs text-gray-500">Success</div>
        <div className="text-lg font-bold text-green-700">{successCount}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Errors</div>
        <div
          className={`text-lg font-bold ${
            errorCount > 0 ? 'text-red-700' : 'text-gray-400'
          }`}
        >
          {errorCount}
        </div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Requested</div>
        <div className="text-lg font-bold text-gray-700">{requested}</div>
      </div>
    </div>
    {children}
  </div>
);

const SubmitResultBox = ({result}: {result: SubmitResult}) => (
  <ResultShell
    title="Wave submitted"
    successCount={result.submitted.length}
    errorCount={result.errors.length}
    requested={result.requested}
  >
    {result.errors.length > 0 && <ErrorsList errors={result.errors} />}
  </ResultShell>
);

const MarkCleanResultBox = ({result}: {result: MarkCleanResult}) => (
  <ResultShell
    title="Wave marked clean"
    successCount={result.marked.length}
    errorCount={result.errors.length}
    requested={result.requested}
  >
    {result.errors.length > 0 && <ErrorsList errors={result.errors} />}
  </ResultShell>
);

const AdvanceResultBox = ({result}: {result: AdvanceResult}) => (
  <ResultShell
    title="Wave advanced"
    successCount={result.advanced.length}
    errorCount={result.errors.length}
    requested={result.requested}
  >
    {result.errors.length > 0 && (
      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
        <div className="text-sm font-medium text-red-800 mb-1">
          {result.errors.length} dataset(s) failed:
        </div>
        <div className="max-h-40 overflow-y-auto text-xs font-mono space-y-1">
          {result.errors.map(e => (
            <div key={e.dataset_id} className="text-red-700">
              <b>{e.dataset_id}</b> — {e.name}
              <br />
              &nbsp;&nbsp;failed at <code>{e.failed_step}</code>, stuck at{' '}
              <code>{e.status}</code>: {e.error}
            </div>
          ))}
        </div>
      </div>
    )}
  </ResultShell>
);

const ErrorsList = ({
  errors,
}: {
  errors: {dataset_id: number; name: string; error?: string}[];
}) => (
  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
    <div className="text-sm font-medium text-red-800 mb-1">Errors</div>
    <div className="max-h-32 overflow-y-auto text-xs font-mono space-y-1">
      {errors.map(e => (
        <div key={e.dataset_id} className="text-red-700">
          <b>{e.dataset_id}</b> — {e.name}: {e.error}
        </div>
      ))}
    </div>
  </div>
);
